import type { SportOption } from '@models';

/**
 * Resuelve la etiqueta legible de un valor de catálogo (`scope`, `modality`…)
 * con una cascada de tres pasos, idéntica a la que aplica
 * `CompetitionTypeLabelPipe` para los tipos de competición:
 *
 *  1. **Opciones del tenant** (`SportConfig.availableScopes` /
 *     `availableModalities`). Es la fuente de verdad: cada deporte renombra y
 *     recorta su catálogo.
 *  2. **Constante global de `@utils`** (`SCOPES` / `MODALITIES`) como fallback
 *     legítimo para valores heredados que ya no están en el catálogo del
 *     tenant pero siguen guardados en Firestore.
 *  3. **El propio valor crudo**, para no dejar nunca la celda vacía. Este era
 *     justo el fallo de los pipes estáticos: fuera de `fmpelota` la constante
 *     no tenía la clave y devolvían `undefined`.
 *
 * A diferencia de `competitionTypeLabels` (un `Record`), las opciones del
 * tenant son `SportOption[]`, así que el paso 1 es una búsqueda por `value`.
 *
 * Función pura y sin dependencias de Angular ni de UI: `libs/utils` lo consume
 * también `apps/functions`. El import de `@models` es `import type`, así que
 * desaparece en runtime.
 *
 * @param options Catálogo del tenant. Puede venir vacío o ausente.
 * @param fallback Constante global indexada por valor.
 * @param value Valor crudo almacenado (p. ej. `'PALETA_CUERO'`).
 * @returns La etiqueta resuelta, o `''` si `value` es vacío/ausente.
 */
export function resolveOptionLabel(
  options: readonly SportOption[] | null | undefined,
  fallback: Readonly<Record<string, string>> | null | undefined,
  value: string | null | undefined,
): string {
  if (!value) return '';
  const tenantLabel = options?.find((o) => o?.value === value)?.label;
  if (tenantLabel) return tenantLabel;
  return fallback?.[value] || value;
}
